import { buildSubFieldRows } from "./productFormSubFieldUtils";

export const PRODUCT_PRIORITY_FIELD_ORDER = [
  "product_code",
  "product_name",
  "vat_id",
  "category_id",
];

export const PRODUCT_RED_FIELD_NAMES = [
  "product_code",
  "product_name",
  "vat_id",
  "category_id",
];

export const PRODUCT_ADD_FORM_DEFAULT_FIELDS = [
  { name: "product_code", label: "Product Code", visible: true, required: true },
  { name: "product_name", label: "Product Name", visible: true, required: true },
  { name: "vat_id", label: "VAT", visible: true, required: true },
  { name: "category_id", label: "Category", visible: true, required: true },
  { name: "brand_id", label: "Brand", visible: true, required: false },
  { name: "size", label: "Size", visible: true, required: true },
  { name: "pack", label: "Pack", visible: true, required: true },
  { name: "box_cost", label: "Box Cost", visible: true, required: true },
  { name: "piece_cost", label: "Piece Cost", visible: true, required: true },
  { name: "recommended_selling_price", label: "Recommended Selling Price", visible: true, required: false },
  { name: "price_box", label: "Price (Box)", visible: true, required: false },
  { name: "special_price_box", label: "Special Price (Box)", visible: true, required: false },
  { name: "box_margin", label: "Box Margin(%)", visible: true, required: false },
  { name: "piece_recommended_selling_price", label: "Recommended Selling Price (Piece)", visible: true, required: false },
  { name: "price_piece", label: "Price (Piece)", visible: true, required: false },
  { name: "special_price_piece", label: "Special Price (Piece)", visible: true, required: false },
  { name: "piece_margin", label: "Piece Margin(%)", visible: true, required: false },
  { name: "product_details", label: "Product Details", visible: true, required: false },
  { name: "unit_id", label: "Unit", visible: true, required: true },
  { name: "min_qty", label: "Min Qty", visible: true, required: false },
  { name: "max_qty", label: "Max Qty", visible: true, required: false },
  { name: "maximum_order_qty", label: "Maximum Order Qty", visible: true, required: false },
  { name: "length", label: "Length", visible: true, required: false },
  { name: "width", label: "Width", visible: true, required: false },
  { name: "height", label: "Height", visible: true, required: false },
  { name: "cbm", label: "CBM", visible: true, required: false },
  { name: "gross_weight", label: "Gross Weight", visible: true, required: false },
  { name: "net_weight", label: "Net Weight", visible: true, required: false },
  { name: "bay", label: "Bay", visible: true, required: false },
  { name: "rack", label: "Rack", visible: true, required: false },
  { name: "part_number", label: "Part Number", visible: true, required: false },
  { name: "inner_ean", label: "Inner EAN", visible: true, required: false },
  { name: "outer_ean", label: "Outer EAN", visible: true, required: false },
  { name: "pallet_ean", label: "Pallet EAN", visible: true, required: false },
  { name: "new_arrival", label: "New Arrival", visible: true, required: false },
  { name: "discount_product", label: "Discount Product", visible: true, required: false },
  { name: "featured", label: "Featured (Shop homepage listing)", visible: true, required: false },
  { name: "product_image", label: "Product Image", visible: true, required: false },
  { name: "available_for", label: "Available For", visible: true, required: false },
  { name: "suppliers", label: "Select Supplier", visible: true, required: false },
];

export const pricingModeStorageKey = (formKey = "add_product") =>
  `product_form_pricing_type_${formKey}`;

export const resolvePricingModeSelection = (raw, options = []) => {
  const token = String(raw ?? "").trim().toLowerCase();
  if (!token) return "";
  if (token === "0" || token === "box") return "box";
  if (token === "1" || token === "piece") return "piece";
  if (token === "2" || token === "both") return "both";
  const match = (options || []).find((option) => {
    const value = String(option.value || "").toLowerCase();
    const label = String(option.label || "").toLowerCase();
    return token === value || token === label;
  });
  return match?.value || "";
};

export const inferPricingModeFromRows = (rows = [], options = []) => {
  const namesOf = (value) => {
    const option = (options || []).find((item) => item.value === value);
    const names = option?.fieldNames;
    if (!names) return new Set();
    return names instanceof Set ? names : new Set(names);
  };
  const boxNames = namesOf("box");
  const pieceNames = namesOf("piece");
  let boxVisible = 0;
  let pieceVisible = 0;
  (rows || []).forEach((row) => {
    if (!row?.visible) return;
    if (boxNames.has(row.name)) boxVisible += 1;
    if (pieceNames.has(row.name)) pieceVisible += 1;
  });
  if (pieceVisible > 0 && boxVisible === 0) return "piece";
  if (boxVisible > 0 && pieceVisible === 0) return "box";
  return "";
};

export const PRODUCT_PRICING_MODE_FILTER = {
  afterField: "pack",
  payloadKey: "pricing_type",
  defaultValue: "box",
  options: [
    {
      value: "box",
      label: "Box",
      fieldNames: [
        "recommended_selling_price",
        "price_box",
        "special_price_box",
        "box_margin",
      ],
      defaultFieldStates: {
        box_cost: { visible: true, required: true },
        piece_cost: { visible: false, required: false },
      },
    },
    {
      value: "piece",
      label: "Piece",
      fieldNames: [
        "piece_recommended_selling_price",
        "price_piece",
        "special_price_piece",
        "piece_margin",
      ],
      defaultFieldStates: {
        box_cost: { visible: false, required: false },
        piece_cost: { visible: true, required: true },
      },
    },
    {
      value: "both",
      label: "Both",
      fieldNames: [
        "recommended_selling_price",
        "price_box",
        "special_price_box",
        "box_margin",
        "piece_recommended_selling_price",
        "price_piece",
        "special_price_piece",
        "piece_margin",
      ],
      defaultFieldStates: {
        box_cost: { visible: true, required: true },
        piece_cost: { visible: true, required: true },
      },
    },
  ],
};

const PRODUCT_SETTINGS_FIELD_ALIASES = {
  code: "product_code",
  name: "product_name",
  details: "product_details",
  split: "product_type",
  unit: "unit_id",
  tax_rate: "vat_id",
  brand: "brand_id",
  category: "category_id",
  min_alert_quantity: "min_qty",
  max_alert_quantity: "max_qty",
  maximum_order_quantity: "maximum_order_qty",
  inner_ean_number: "inner_ean",
  outer_ean_number: "outer_ean",
  pallete_ean: "pallet_ean",
  is_discount_allow: "discount_product",
  availableFor: "available_for",
  product_code: "product_code",
  product_name: "product_name",
  product_details: "product_details",
  product_type: "product_type",
  unit_id: "unit_id",
  vat_id: "vat_id",
  brand_id: "brand_id",
  category_id: "category_id",
  min_qty: "min_qty",
  max_qty: "max_qty",
  maximum_order_qty: "maximum_order_qty",
  inner_ean: "inner_ean",
  outer_ean: "outer_ean",
  pallet_ean: "pallet_ean",
  discount_product: "discount_product",
  available_for: "available_for",
  box_pricing: "box_prices",
  box_price_bands: "box_prices",
  box_prices: "box_prices",
  piece_pricing: "piece_prices",
  piece_price_bands: "piece_prices",
  piece_prices: "piece_prices",
  both: "both",
  both_prices: "both",
  both_pricing: "both",
  suppliers: "suppliers",
  selected_suppliers: "suppliers",
  selected_supplier: "suppliers",
  selectedsuppliers: "suppliers",
  selectedsupplier: "suppliers",
  select_supplier: "suppliers",
  select_suppliers: "suppliers",
  selectsupplier: "suppliers",
  selectsuppliers: "suppliers",
  supplier: "suppliers",
  supplier_id: "suppliers",
  supplier_ids: "suppliers",
  supplierid: "suppliers",
  supplierids: "suppliers",
  box_cost: "box_cost",
  cost: "box_cost",
  piece_cost: "piece_cost",
  recommended_selling_price: "recommended_selling_price",
  recommendedsellingboxprice: "recommended_selling_price",
  recommended_selling_box_price: "recommended_selling_price",
  piece_recommended_selling_price: "piece_recommended_selling_price",
  recommendedsellingpieceprice: "piece_recommended_selling_price",
  recommended_selling_piece_price: "piece_recommended_selling_price",
  price_box: "price_box",
  box_price: "price_box",
  special_price_box: "special_price_box",
  special_box_price: "special_price_box",
  price_piece: "price_piece",
  piece_price: "price_piece",
  special_price_piece: "special_price_piece",
  special_piece_price: "special_price_piece",
  piece_margin: "piece_margin",
  box_margin: "box_margin",
  margin: "box_margin",
};

export const resolveProductSettingsFieldName = (apiName = "") => {
  const trimmed = String(apiName || "").trim();
  if (!trimmed) return "";
  if (PRODUCT_SETTINGS_FIELD_ALIASES[trimmed]) {
    return PRODUCT_SETTINGS_FIELD_ALIASES[trimmed];
  }
  const raw = trimmed.toLowerCase().replace(/\s+/g, "_");
  if (PRODUCT_SETTINGS_FIELD_ALIASES[raw]) {
    return PRODUCT_SETTINGS_FIELD_ALIASES[raw];
  }
  const compact = raw.replace(/_/g, "");
  if (PRODUCT_SETTINGS_FIELD_ALIASES[compact]) {
    return PRODUCT_SETTINGS_FIELD_ALIASES[compact];
  }
  if (raw.includes("supplier")) return "suppliers";
  return raw;
};

export const mapDefaultProductFieldRows = (
  defaults = PRODUCT_ADD_FORM_DEFAULT_FIELDS,
) =>
  (defaults || []).map((field) => ({
    name: field.name,
    label: field.label || field.name,
    section: "",
    visible: field.visible !== false,
    required: field.required === true,
    subFields: buildSubFieldRows(field.name, []),
  }));

export const applyProductPriorityFieldDefaults = (rows = []) => {
  const byName = new Map();
  (rows || []).forEach((row) => {
    let key = resolveProductSettingsFieldName(row?.name || "");
    const labelKey = String(row?.label || "").toLowerCase();
    if (labelKey.includes("supplier")) key = "suppliers";
    if (!key || byName.has(key)) {
      if (!key || !byName.has(key)) return;
      const current = byName.get(key);
      const hidden = current.visible === false || row.visible === false;
      const sourceNames = [
        ...new Set([...(current.sourceNames || []), ...(row.sourceNames || []), row.name, current.name].filter(Boolean)),
      ];
      byName.set(key, {
        ...current,
        ...row,
        name: key,
        sourceNames,
        visible: hidden ? false : row.visible !== false,
        required: hidden ? false : row.required === true,
        label: key === "suppliers" ? "Select Supplier" : row.label || current.label,
      });
      return;
    }
    byName.set(key, { ...row, name: key });
  });

  const defaultByName = new Map(
    mapDefaultProductFieldRows().map((row) => [row.name, row]),
  );
  const ordered = [];
  const used = new Set();

  PRODUCT_PRIORITY_FIELD_ORDER.forEach((name) => {
    const existing = byName.get(name) || defaultByName.get(name);
    if (!existing) return;
    ordered.push({
      ...existing,
      name,
      label: defaultByName.get(name)?.label || existing.label || name,
      visible: true,
      required: true,
      subFields: existing.subFields || defaultByName.get(name)?.subFields || [],
    });
    used.add(name);
  });

  PRODUCT_ADD_FORM_DEFAULT_FIELDS.forEach((field) => {
    if (used.has(field.name)) return;
    const existing = byName.get(field.name) || defaultByName.get(field.name);
    if (!existing) return;
    ordered.push({
      ...existing,
      name: field.name,
      label:
        field.name === "suppliers"
          ? field.label
          : existing.label || field.label || field.name,
    });
    used.add(field.name);
  });

  byName.forEach((row, name) => {
    if (used.has(name)) return;
    const resolved = resolveProductSettingsFieldName(name);
    if (used.has(resolved)) return;
    const labelKey = String(row.label || "").toLowerCase();
    if (labelKey.includes("supplier") && used.has("suppliers")) return;
    ordered.push(row);
    used.add(name);
    if (resolved) used.add(resolved);
  });

  return ordered;
};
